import { MatPaginatorIntl } from '@angular/material/paginator';
import { BaseMatPaginatorIntl } from '@iot-platform/table-engine';
import { LicenseManager as AgGridLicenseManager } from 'ag-grid-enterprise';
import { CommonModule } from '@angular/common';
import { EnvironmentProviders, inject, makeEnvironmentProviders, NgModule, provideEnvironmentInitializer } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { BrowserModule, DomSanitizer, Title } from '@angular/platform-browser';
import { provideAuthModule } from '@iot-platform/auth';
import { ApiInterceptorModule, LocalStorageModule, provideDefaultProvidersToken, provideIotHighcharts, provideIotNativeDateAdapter } from '@iot-platform/core';
import { PreferencesModule, UserPreferencesFavoriteFilterEngineService } from '@iot-platform/data-access/users';
import { I18nModule } from '@iot-platform/i18n';
import { FilterEngineV2Module } from '@iot-platform/iot-platform-ui';
import { AppName } from '@iot-platform/models/common';
import { NotificationModule } from '@iot-platform/notification';
import { ConfigModule as OyanConfigModule } from '@iot-platform/oyan-ui';
import { EffectsModule } from '@ngrx/effects';
import { DEFAULT_ROUTER_FEATURENAME, FullRouterStateSerializer, provideRouterStore, routerReducer } from '@ngrx/router-store';
import { MetaReducer, provideStore, StoreModule } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { AppRouterModule } from './app.router.module';

export const metaReducers: MetaReducer<unknown>[] = [];

const storeSettings = {
  metaReducers,
  runtimeChecks: {
    strictStateImmutability: false,
    strictActionImmutability: false,
    strictStateSerializability: false,
    strictActionSerializability: false
  }
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    CommonModule,
    LocalStorageModule.forRoot({ prefix: environment.storage.appPrefix }),
    AppRouterModule,
    StoreModule.forRoot({}, storeSettings),
    EffectsModule.forRoot([]),
    OyanConfigModule.forRoot({
      front: { version: '1.0.0' },
      back: { version: '1.0.0' }
    }),
    I18nModule.forRoot({ langs: ['fr', 'en', 'de', 'it', 'es', 'pt', 'nl'] }),
    NotificationModule,
    PreferencesModule,
    ApiInterceptorModule.forRoot({
      excludedApis: ['/admin-profiles', '/grids', '/favorite-views', '/v2undefined', '/entities/undefined/admin-profiles', '/directory/users']
    }),
    FilterEngineV2Module.withSettings({
      userPreferencesService: UserPreferencesFavoriteFilterEngineService
    })
  ],
  providers: [
    provideApplicationInitializer(),
    provideIotHighcharts(),
    provideDefaultProvidersToken({ environment }),
    provideStore(
      {
        [DEFAULT_ROUTER_FEATURENAME]: routerReducer
      },
      storeSettings
    ),
    provideIotNativeDateAdapter(),
    provideStoreDevtools({ maxAge: 20, logOnly: environment.production }),
    provideRouterStore({ serializer: FullRouterStateSerializer }),
    provideAuthModule({
      appName: AppName.OYAN,
      rememberMe: true
    }),
    {
      provide: MatPaginatorIntl,
      useClass: BaseMatPaginatorIntl
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}

function provideApplicationInitializer(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideEnvironmentInitializer(() => {
      AgGridLicenseManager.setLicenseKey(environment.apiKeys.agGrid);
      inject(MatIconRegistry).addSvgIconSet(inject(DomSanitizer).bypassSecurityTrustResourceUrl('assets/svg/iot4bos-icon-set.svg'));
      inject(MatIconRegistry).addSvgIconSet(inject(DomSanitizer).bypassSecurityTrustResourceUrl('assets/svg/oyan-icon-set.svg'));
      if (environment.envType !== 'prod') {
        const link: HTMLLinkElement | null = document.querySelector('link[rel="icon"]');
        if (link) {
          link.href = `/assets/favicons/favicon-${environment.envType}.ico`;
        }
        const titleService = inject(Title);
        titleService.setTitle(`${titleService.getTitle()} - ${environment.envName}`);
      }
    })
  ]);
}
